import React from 'react'
import { useTranslation } from 'react-i18next'
import MetricCategoryItem, { MetricCategoryItemStrings } from 'enums/MetricCategoryItem'
import { MetricItem } from 'enums/MetricItem'
import { useDisplayTheme } from 'hooks/useUiState'
import { Theme } from 'features/ui/types'
// @ts-ignore
import { SimpleBar } from '@netbasequid/chart'
// @ts-ignore
import { getValue } from 'utils/metricCategoryItemUtil'
import { InsightChartProps, SideBySideSimplifiedData, SideBySideSelectionType } from '../../types'
import {
  getDisplayInsightValue,
  getDisplayAvg,
  getStarRatingName,
  getSortedSetBreakInfo,
  getPercentageByIndexandBreakInfo
} from '../utils'
import {
  InsightChartContainer,
  InsightContainer,
  LabelContainer,
  LabelName,
  LabelNameWithTooltip,
  LabelValue,
  Star
} from './styles'

const STAR_RATING_COLORS = [
  'metric.starRating.fiveStar',
  'metric.starRating.fourStar',
  'metric.starRating.threeStar',
  'metric.starRating.twoStar',
  'metric.starRating.oneStar',
  'metric.starRating.noResponse'
]

export const DisplayedName = ({ name }: { name: MetricCategoryItemStrings }) => {
  const theme = useDisplayTheme()
  return (
    <>
      {getStarRatingName(name)}
      {name !== MetricCategoryItem.NO_RESPONSE_STAR && <Star mode={theme as Theme} />}
    </>
  )
}

const getSetInfo = (data: SideBySideSimplifiedData) => {
  const { set, topicId, topicName, themeId, themeName, tagName, docName } = data
  const sortedSet = [...set].sort((a, b) => b.value! - a.value!)

  const breakInfo = getSortedSetBreakInfo(sortedSet)

  let total = 0
  let sum = 0

  const setInfoMap: Record<MetricCategoryItemStrings, SideBySideSelectionType> = {} as Record<
    MetricCategoryItemStrings,
    SideBySideSelectionType
  >

  sortedSet.forEach((insightData, idx) => {
    const name = insightData.name! as MetricCategoryItemStrings
    const percentage = getPercentageByIndexandBreakInfo(
      {
        value: insightData.value!,
        totalBars: sortedSet.length,
        index: idx
      },
      breakInfo
    )

    if (insightData.name! !== MetricCategoryItem.NO_RESPONSE_STAR) {
      total += insightData.value!
      sum += insightData.value! * getValue(name)
    }

    setInfoMap[name] = {
      name,
      value: insightData.value!,
      insightType: insightData.insightType!,
      percentage,
      insight: insightData,
      topicId,
      topicName,
      themeId,
      themeName,
      tagName,
      docName
    }
  })

  const setInfos = MetricItem.CHILDREN_STAR_RATING.map(
    (starRatingName: MetricCategoryItemStrings) => setInfoMap[starRatingName]
  )

  return {
    hasBreakSymbol: breakInfo.hasBreakSymbol,
    setInfos,
    total,
    avg: sum / total
  }
}

const StarRatingChart = ({ data, onCellClick }: InsightChartProps) => {
  const theme = useDisplayTheme()
  const { t } = useTranslation()

  if (!data) return null
  const { hasBreakSymbol, setInfos, avg, total } = getSetInfo(data)

  return (
    <InsightChartContainer>
      {setInfos?.map((insightData, idx) => (
        <InsightContainer key={insightData.name} mode={theme} clickable={insightData.value > 0}>
          <LabelContainer onClick={() => onCellClick(insightData)}>
            <LabelName>
              <DisplayedName name={insightData.name as MetricCategoryItemStrings} />
            </LabelName>
            <LabelValue>{getDisplayInsightValue(insightData.value)}</LabelValue>
          </LabelContainer>
          <SimpleBar
            progress={insightData.percentage}
            colorSetting={STAR_RATING_COLORS[idx]}
            mode={theme}
            breakSymbolType="BreakDoubleSlash100"
            breakSymbolPosition={hasBreakSymbol ? 70 : undefined}
            breakSymbolTooltipContent={t('breakSymbolTooltip')}
            height="2px"
          />
        </InsightContainer>
      ))}
      <InsightContainer displayType="average" mode={theme}>
        <LabelContainer>
          <LabelNameWithTooltip type="weak">{t('average')}:</LabelNameWithTooltip>
          <LabelValue type="weak">{getDisplayAvg(avg)}</LabelValue>
        </LabelContainer>
      </InsightContainer>

      <InsightContainer displayType="total" mode={theme}>
        <LabelContainer>
          <LabelNameWithTooltip type="weak">{t('totalResponse')}:</LabelNameWithTooltip>
          <LabelValue type="weak">{getDisplayInsightValue(total)}</LabelValue>
        </LabelContainer>
      </InsightContainer>
    </InsightChartContainer>
  )
}
export default StarRatingChart
